<template>
  <div class="row">
    <div class="col-md-12 col-lg-12 ">
      <div class="search-bar">
        <div class="col-sm-12 no-padding">
            <div class="pull-left">
              <el-input v-model="searchQuery" @input="onInput()" :placeholder="$t('reports.search')" class="icon-search"></el-input>
            </div>
            <div class="pull-right">
              <router-link class="btn add " :title="$t('general.add') + ' ' + $t('marketingAutomation.single')" :to="{ path: '/marketing/automation/add'}">
                <img src="/static/img/icons/add.png">
                {{ $t('general.add') }}
              </router-link>
            </div>
          </div>
      </div>
      <div class="card">
        <div class="card-content row">
          <data-tables class="automation-table width-table"
                    v-loading="loading"
                    :data="tableData"
                    :pagination-def="paginationDef"
                    :table-props="{'border': false}"
                    @row-click="goToRecord"
                    style="width: 100%">
            <!-- Name + type -->
            <el-table-column
              :min-width="240"
              :label="$t('marketingAutomation.name')">
              <template slot-scope="props">
                <div class="automation-cell-name">
                  <span class="automation-type-icon"><i :class="typeIcon(props.row.type)"></i></span>
                  <div>
                    <div class="automation-name">{{ props.row.name }}</div>
                    <div class="automation-sub">{{ typeLabel(props.row.type) }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <!-- Period: state on the first row, the dates under it -->
            <el-table-column
              :min-width="200"
              :label="$t('marketingAutomation.period')">
              <template slot-scope="props">
                <div class="automation-state-row">
                  <span class="automation-state" :class="'automation-state--' + props.row.state">
                    <span class="automation-state__dot"></span>{{ $t('marketingAutomation.state_' + props.row.state) }}
                  </span>
                  <span class="automation-sub" v-if="periodHint(props.row)">{{ periodHint(props.row) }}</span>
                </div>
                <div class="automation-period">
                  <i class="fa fa-calendar-o"></i>
                  {{ formatDay(props.row.date_from) }} – {{ formatDay(props.row.date_to) }}
                </div>
              </template>
            </el-table-column>
            <!-- Coupons -->
            <el-table-column
              :min-width="230"
              :label="$t('marketingAutomation.coupons')">
              <template slot-scope="props">
                <!-- max 2 coupons visible (the row has a fixed height), the rest as "+N" with all names in the tooltip -->
                <div class="automation-badges">
                  <span v-for="coupon in (props.row.coupons || []).slice(0, 2)"
                        :key="coupon.id"
                        class="automation-badge"
                        :class="{ 'automation-badge--off': !coupon.active }"
                        :title="coupon.active ? coupon.name : coupon.name + ' (' + $t('marketingAutomation.couponInactive') + ')'">
                    <i class="fa fa-ticket"></i><span class="automation-badge__text">{{ coupon.name }}</span>
                  </span>
                  <span v-if="(props.row.coupons || []).length > 2"
                        class="automation-badge automation-badge--more"
                        :title="props.row.coupons.map(coupon => coupon.name).join(', ')">
                    +{{ props.row.coupons.length - 2 }}
                  </span>
                </div>
              </template>
            </el-table-column>
            <!-- Beneficiaries: counters + link to the paginated list -->
            <el-table-column
              :min-width="190"
              :label="$t('marketingAutomation.beneficiaries')">
              <template slot-scope="props">
                <!-- forgotten cart (type 3): sent / coupon used / carts ordered -->
                <div class="automation-benef-counts" v-if="props.row.type === 3">
                  <span :title="$t('marketingAutomation.statusAllSent')"><i class="fa fa-paper-plane"></i> {{ (props.row.grants && props.row.grants.all) || 0 }}</span>
                  <span :title="$t('marketingAutomation.cartStatusUsed')"><i class="fa fa-check-circle"></i> {{ (props.row.grants && props.row.grants.used) || 0 }}</span>
                  <span :title="$t('marketingAutomation.status_converted')"><i class="fa fa-shopping-bag"></i> {{ (props.row.grants && props.row.grants.converted) || 0 }}</span>
                </div>
                <div class="automation-benef-counts" v-else>
                  <span :title="$t('marketingAutomation.status_granted')"><i class="fa fa-gift"></i> {{ (props.row.grants && props.row.grants.granted) || 0 }}</span>
                  <span :title="$t('marketingAutomation.status_used')"><i class="fa fa-check-circle"></i> {{ (props.row.grants && props.row.grants.used) || 0 }}</span>
                  <span :title="$t('marketingAutomation.status_expired')"><i class="fa fa-clock-o"></i> {{ (props.row.grants && props.row.grants.expired) || 0 }}</span>
                </div>
                <a class="automation-benef-link" @click.stop.prevent="openBeneficiaries(props.row)">
                  {{ $t('marketingAutomation.viewBeneficiaries') }} <i class="fa fa-angle-right"></i>
                </a>
              </template>
            </el-table-column>
            <el-table-column
              :width="100"
              class-name="centered-cell"
              align="center"
              header-align="center"
              :label="$t('marketingAutomation.status')">
              <template slot-scope="props">
                <div @click.stop>
                  <p-switch v-model="props.row.active" type="success" @input="handleActive(props.row)"></p-switch>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              :width="90"
              class-name="centered-cell"
              align="center"
              header-align="center"
              :label="$t('marketingCampaigns.actions')">
              <template slot-scope="props">
                <a class="btn btn-icon-small btn-danger" :title="$t('general.delete')" @click.stop="handleDelete(props.row)"><i class="fa fa-trash-o"></i></a>
              </template>
            </el-table-column>
            <el-table-column
              :width="160"
              class-name="centered-cell"
              align="center"
              header-align="center">
              <template slot-scope="props">
                <div class="edit">
                  <i class="fa fa-pencil"></i>{{$t('general.edit')}}
                </div>
              </template>
            </el-table-column>
          </data-tables>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Input} from 'element-ui'
  import DataTables from 'vue-data-tables'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  import { AUTOMATION_TYPES } from './automationTypes'
  Vue.use(Input)
  Vue.use(DataTables)

  export default {
    components: {
      PSwitch
    },
    data () {
      return {
        loading: true,
        searchQuery: '',
        tableData: [],
        paginationDef: {
          pageSize: 20,
          currentPage: 1,
          pageSizes: [20, 50, 100]
        }
      }
    },
    created () {
      this.getCampaigns()
      this.$bus.$on('refresh', this.getCampaigns)
    },
    beforeDestroy () {
      this.$bus.$off('refresh', this.getCampaigns)
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      typeLabel (type) {
        const item = AUTOMATION_TYPES.find(t => t.value === type)
        return item ? this.$t(item.label) : type
      },
      typeIcon (type) {
        const item = AUTOMATION_TYPES.find(t => t.value === type)
        return item ? item.icon : 'fa fa-cog'
      },
      formatDay (value) {
        if (!value) {
          return '-'
        }
        const parts = String(value).substr(0, 10).split('-')
        return parts.length === 3 ? parts[2] + '.' + parts[1] + '.' + parts[0] : value
      },
      // "ends in X days" / "starts in X days" under the state
      periodHint (row) {
        const today = new Date()
        const day = (value) => {
          const parts = String(value || '').substr(0, 10).split('-').map(Number)
          return parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : null
        }
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        const diff = (target) => Math.round((target - start) / 86400000)
        // only when it is close (a far end date says nothing useful)
        const NEAR_DAYS = 30
        if (row.state === 'running' && day(row.date_to)) {
          const days = diff(day(row.date_to))
          if (days === 0) {
            return this.$t('marketingAutomation.endsToday')
          }
          return days <= NEAR_DAYS ? this.$t('marketingAutomation.endsIn', { days }) : ''
        }
        if (row.state === 'scheduled' && day(row.date_from)) {
          const days = diff(day(row.date_from))
          return days <= NEAR_DAYS ? this.$t('marketingAutomation.startsIn', { days }) : ''
        }
        return ''
      },
      getCampaigns () {
        const data = this.requestData()
        if (this.searchQuery.length >= 3) {
          data.search = this.searchQuery
        }
        this.loading = true
        this.apiRequest('/automation-campaign', 'GET', data, (response) => {
          if (response.status === true) {
            this.tableData = response.data || []
          }
          this.$nextTick(() => {
            this.loading = false
          })
        })
      },
      onInput () {
        if (this.searchQuery.length >= 3 || this.searchQuery.length === 0) {
          this.getCampaigns()
        }
      },
      openBeneficiaries (row) {
        this.$router.push({ path: '/marketing/automation/beneficiaries', query: { campaign_id: row.id } }).catch(() => {})
      },
      goToRecord (row, column, event) {
        this.globalGoToRecord(row, event, column, '/marketing/automation/edit/')
      },
      errorText (response) {
        const errors = (response && response.errors) || {}
        const messages = Object.keys(errors).map(key => Array.isArray(errors[key]) ? errors[key][0] : errors[key])
        return messages.join(' ') || this.$t('validations.wentWrong')
      },
      handleActive (row) {
        const data = {
          ...this.requestData(),
          active: row.active ? 1 : 0
        }
        this.apiRequest('/automation-campaign/' + row.id + '/status', 'POST', data, (response) => {
          if (response.status) {
            swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          } else {
            row.active = !row.active
            swal('', this.errorText(response), 'error').catch(() => {})
          }
        })
      },
      handleDelete (row) {
        swal({
          text: this.$t('marketingAutomation.areYouSureDelete', { name: row.name }),
          type: 'question',
          showCancelButton: true
        }).then((result) => {
          if (!result) {
            return
          }
          this.apiRequest('/automation-campaign/' + row.id, 'DELETE', this.requestData(), (response) => {
            if (response.status === true) {
              swal('', this.$t('marketingAutomation.deleted'), 'success').catch(() => {})
              this.tableData = this.tableData.filter(item => item.id !== row.id)
            } else {
              swal('', this.errorText(response), 'error').catch(() => {})
            }
          })
        }).catch(() => {})
      }
    }
  }
</script>
<style>
  /*
   * Every .cell has its own white background (that is the "row"), so all the
   * cells of a row must have the SAME height: fixed 64px (the global rule is 50px),
   * content vertically centered and left aligned.
   */
  .automation-table .el-table tbody td .cell {
    height: 64px !important;
    display: flex !important;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    text-align: left;
    white-space: normal;
    line-height: 1.35;
    overflow: hidden;
    padding-left: 14px;
    padding-right: 10px;
  }
  .automation-table .el-table tbody td.centered-cell .cell {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
  }
  .automation-table .el-table th .cell {
    padding-left: 14px;
  }
  .automation-table .el-table th.centered-cell .cell {
    padding-left: 0;
  }
  .automation-sub {
    font-size: 12px;
    color: #8a8a9a;
  }
  /* name + type */
  .automation-cell-name {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .automation-type-icon {
    flex: 0 0 38px;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #f3eefb;
    color: #7e57c2;
    font-size: 17px;
  }
  .automation-cell-name > div {
    min-width: 0;
  }
  .automation-name {
    font-weight: 600;
    color: #2c2c3a;
    line-height: 1.3;
    /* max 2 lines inside the fixed row height */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /* period + state */
  .automation-period {
    margin-top: 4px;
    white-space: nowrap;
    font-size: 12px;
    color: #6b6b7b;
  }
  .automation-period .fa {
    color: #9e9eb0;
    margin-right: 4px;
  }
  .automation-state-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
  }
  .automation-state {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    border: 1px solid #e0e0e0;
    background: #f5f5f5;
    color: #777;
  }
  .automation-state__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
  .automation-state--running {
    background: #eef7ee;
    color: #2e7d32;
    border-color: #c8e6c9;
  }
  .automation-state--scheduled {
    background: #e3f2fd;
    color: #1565c0;
    border-color: #bbdefb;
  }
  /* beneficiaries */
  .automation-benef-counts {
    display: flex;
    gap: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #2c2c3a;
  }
  .automation-benef-counts .fa-gift { color: #1565c0; }
  .automation-benef-counts .fa-check-circle { color: #2e7d32; }
  .automation-benef-counts .fa-clock-o { color: #9e9e9e; }
  .automation-benef-counts .fa-paper-plane { color: #7e57c2; }
  .automation-benef-counts .fa-shopping-bag { color: #2e7d32; }
  .automation-benef-link {
    margin-top: 3px;
    font-size: 12px;
    color: #7e57c2;
    cursor: pointer;
  }
  .automation-benef-link:hover {
    text-decoration: underline;
  }
  /* coupons */
  .automation-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-width: 100%;
  }
  .automation-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    max-width: 100%;
    padding: 2px 9px;
    border-radius: 12px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
    background: #ede7f6;
    color: #5e35b1;
    border: 1px solid #d1c4e9;
  }
  .automation-badge__text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .automation-badge--more {
    background: #5e35b1;
    color: #fff;
    border-color: #5e35b1;
    font-weight: 600;
  }
  .automation-badge--off {
    background: #f5f5f5;
    color: #999;
    border-color: #e0e0e0;
    text-decoration: line-through;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/AutomationCampaigns/AutomationCampaigns.vue